"use client";

import { RotateCcw, RotateCw, Trash2, Undo2 } from "lucide-react";
import { useState } from "react";
import { BlobImage } from "@/components/BlobImage";
import { primaryButtonClass } from "@/components/form";
import { ProgressBar } from "@/components/ProgressBar";
import { moveItem, SortableList } from "@/components/SortableList";
import { ToolShell, type ToolResult } from "@/components/ToolShell";
import { usePdfPages } from "@/hooks/usePdfPages";
import { useToolFiles } from "@/hooks/useToolFiles";
import { outputName } from "@/lib/files";
import { describeChanges, type PagePlan, type Rotation } from "@/lib/pdf/page-plan";
import { getTool } from "@/lib/tools";
import { cn } from "@/lib/utils";
import { rebuildFile } from "@/workers/pdf-client";
const tool = getTool("organize-pdf")!;
const turn = (r: Rotation, by: number) => ((r + by) % 360 + 360) % 360 as Rotation;

/** FR-12: page grid, drag to reorder, rotate, delete, multi-select, undo. */
export function OrganizeTool() {
  const {
    files,
    setFiles,
    notices
  } = useToolFiles(tool);
  const file = files[0];
  const {
    count,
    thumbs,
    error: loadError
  } = usePdfPages(file);
  // The edited page list belongs to one file; a new file starts fresh.
  const [edit, setEdit] = useState<{
    file: File;
    pages: PagePlan[];
    history: PagePlan[][];
  } | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [results, setResults] = useState<ToolResult[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const original: PagePlan[] = Array.from({
    length: count ?? 0
  }, (_, index) => ({
    index,
    rotate: 0
  }));
  const current = edit?.file === file ? edit : null;
  const pages = current?.pages ?? original;
  const history = current?.history ?? [];
  function apply(next: PagePlan[]) {
    if (!file) return;
    setEdit({
      file,
      pages: next,
      history: [...history, pages]
    });
    setResults(null);
  }
  function undo() {
    if (!file || history.length === 0) return;
    setEdit({
      file,
      pages: history.at(-1)!,
      history: history.slice(0, -1)
    });
    setSelected(new Set());
  }
  const targets = (index_0?: number) => index_0 !== undefined ? new Set([index_0]) : selected;
  function rotate(by: number, index_1?: number) {
    const which = targets(index_1);
    apply(pages.map(p => which.has(p.index) ? {
      ...p,
      rotate: turn(p.rotate, by)
    } : p));
  }
  function remove(index_2?: number) {
    const which_0 = targets(index_2);
    const next_0 = pages.filter(p_0 => !which_0.has(p_0.index));
    if (next_0.length === 0) {
      setError("A PDF needs at least one page, so the last page can't be deleted.");
      return;
    }
    setError("");
    apply(next_0);
    setSelected(new Set([...selected].filter(i => !which_0.has(i))));
  }
  function toggle(index_3: number) {
    const next_1 = new Set(selected);
    if (next_1.has(index_3)) next_1.delete(index_3);else next_1.add(index_3);
    setSelected(next_1);
  }
  async function save() {
    if (!file) return;
    setBusy(true);
    setError("");
    try {
      const blob = await rebuildFile(file, pages);
      setResults([{
        blob,
        name: outputName(file.name, "organized", "pdf"),
        detail: `${pages.length} ${pages.length === 1 ? "page" : "pages"}`
      }]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const changes = describeChanges(count ?? 0, pages);
  const toolbarButton = "inline-flex min-h-11 items-center gap-2 rounded-lg border-[1.5px] border-line-strong bg-card px-3 text-sm font-medium hover:bg-canvas disabled:opacity-40 disabled:hover:bg-card";
  const iconButton = "flex size-11 items-center justify-center rounded-lg text-ink hover:bg-sand";
  return <ToolShell tool={tool} files={files} onFilesChange={next_2 => {
    setFiles(next_2);
    setResults(null);
    setSelected(new Set());
    setError("");
  }} notices={notices} results={results} onStartOver={() => {
    setFiles([]);
    setEdit(null);
    setResults(null);
    setSelected(new Set());
  }}>
      {loadError ? <p role="alert" className="font-medium text-destructive">
          {loadError}
        </p> : count === undefined ? <ProgressBar label="Opening your PDF…" /> : <div className="flex flex-col gap-4">
          <div role="toolbar" aria-label="Page actions" className="flex flex-wrap items-center gap-2">
            <button type="button" className={toolbarButton} onClick={() => setSelected(selected.size === pages.length ? new Set() : new Set(pages.map(p_1 => p_1.index)))}>
              {selected.size === pages.length ? "Select none" : "Select all"}
            </button>
            <button type="button" className={toolbarButton} disabled={selected.size === 0} onClick={() => rotate(-90)}>
              <RotateCcw className="size-4" aria-hidden="true" />
              Turn left
            </button>
            <button type="button" className={toolbarButton} disabled={selected.size === 0} onClick={() => rotate(90)}>
              <RotateCw className="size-4" aria-hidden="true" />
              Turn right
            </button>
            <button type="button" className={toolbarButton} disabled={selected.size === 0} onClick={() => remove()}>
              <Trash2 className="size-4" aria-hidden="true" />
              Delete
            </button>
            <button type="button" className={toolbarButton} disabled={history.length === 0} onClick={undo}>
              <Undo2 className="size-4" aria-hidden="true" />
              Undo
            </button>
            <span className="text-sm text-muted-foreground" aria-live="polite">
              {selected.size > 0 ? `${selected.size} selected` : ""}
            </span>
          </div>

          <SortableList layout="grid" className="grid-cols-[repeat(auto-fill,minmax(148px,1fr))] sm:grid-cols-[repeat(auto-fill,minmax(180px,1fr))]" label="Pages, in order" items={pages} itemKey={p_2 => String(p_2.index)} itemLabel={p_3 => `Page ${p_3.index + 1}`} onReorder={(from, to) => apply(moveItem(pages, from, to))} renderItem={(p_4, i_0, {
        handle,
        moveButtons
      }) => {
        const thumb = thumbs[p_4.index];
        const isSelected = selected.has(p_4.index);
        return <div data-testid="page" data-page={p_4.index + 1} className={cn("flex flex-col gap-1 rounded-[14px] border-2 bg-card p-1.5", isSelected ? "border-brand bg-brand-tint" : "border-transparent")}>
                  <div className="flex items-center justify-between">
                    <label className="flex min-h-11 cursor-pointer items-center gap-2 px-1.5 text-sm font-semibold">
                      <input type="checkbox" className="size-5 accent-brand" checked={isSelected} onChange={() => toggle(p_4.index)} />
                      Page {p_4.index + 1}
                    </label>
                    {handle}
                  </div>
                  <div className="flex aspect-square items-center justify-center overflow-hidden rounded-lg bg-[#e6e9f2] p-2">
                    {thumb ? <BlobImage blob={thumb} alt={`Page ${p_4.index + 1}${p_4.rotate ? `, turned ${p_4.rotate}°` : ""}`} className="max-h-full max-w-full shadow transition-transform" style={{
              transform: `rotate(${p_4.rotate}deg)`
            }} /> : <span className="h-4/5 w-3/5 animate-pulse rounded bg-sand" />}
                  </div>
                  <div className="flex flex-wrap items-center justify-between">
                    <span className="flex">{moveButtons}</span>
                    <span className="flex">
                      <button type="button" className={iconButton} aria-label={`Turn page ${p_4.index + 1} right`} onClick={() => rotate(90, p_4.index)}>
                        <RotateCw className="size-5" aria-hidden="true" />
                      </button>
                      <button type="button" className={iconButton} aria-label={`Delete page ${p_4.index + 1}`} onClick={() => remove(p_4.index)}>
                        <Trash2 className="size-5" aria-hidden="true" />
                      </button>
                    </span>
                  </div>
                  <span className="sr-only">Position {i_0 + 1}</span>
                </div>;
      }} />

          <section aria-label="Save" className="sticky bottom-0 z-20 flex flex-col gap-3 rounded-[16px] border bg-card p-4 shadow-[0_-4px_12px_rgba(31,36,48,0.06)] sm:flex-row sm:items-center sm:justify-between sm:p-5">
            <div className="flex flex-col">
              <span className="font-semibold" data-testid="changes">
                {changes}
              </span>
              <span className="text-sm text-muted-foreground">
                {pages.length} of {count} pages · nothing is uploaded
              </span>
              {error && <span role="alert" className="text-sm font-medium text-destructive">
                  {error}
                </span>}
            </div>
            {busy ? <div className="sm:w-64">
                <ProgressBar label="Saving your PDF…" />
              </div> : <button type="button" onClick={save} className={cn(primaryButtonClass, "sm:w-auto")}>
                Save PDF
              </button>}
          </section>
        </div>}
    </ToolShell>;
}